import { Dialog } from "components/dialog";
import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";
import PureDropDown from "components/pure-dropdown";
import { createPackage, getTags, updatePackage } from "../api";
import { Package, PackageType, PublishStatus } from "../types";
import { useEffect, useRef, useState } from "react";
import { AiOutlineClose, AiOutlineLoading } from "react-icons/ai";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { handleAxiosAndCaughtError } from "utils";

interface PackageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  package: Package | null;
}

const PackageDialog = ({
  isOpen,
  onClose,
  package: pkg,
}: PackageDialogProps) => {
  const { t } = useTranslation();

  // Form data state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    isPublished: false,
    selectedTags: [] as string[],
  });
  // Tag input state
  const [tagInput, setTagInput] = useState("");

  // File state
  const [fileData, setFileData] = useState({
    iconFile: null as File | null,
    zipFile: null as File | null,
    zipFileName: "",
    iconPreview: null as string | null,
  });

  // UI state
  const [uiState, setUiState] = useState({
    loading: false,
    error: null as string | null,
    formErrors: {} as Record<string, string>,
  });

  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  const zipFileInputRef = useRef<HTMLInputElement>(null);
  const iconFileInputRef = useRef<HTMLInputElement>(null);
  const isEditMode = !!pkg;

  const packageTypes: { value: PackageType; label: string }[] = [
    { value: "agent", label: t("marketplace.typeAgent") },
  ];

  useEffect(() => {
    if (isOpen) {
      setUiState({ loading: true, error: null, formErrors: {} });

      // Reset form state
      setFormData({
        name: "",
        description: "",
        type: "agent",
        isPublished: false,
        selectedTags: [],
      });
      setTagInput("");

      setFileData({
        iconFile: null,
        zipFile: null,
        zipFileName: "",
        iconPreview: null,
      });

      // Fetch tags for potential future use
      getTags()
        .then((tagsData) => {
          setTags(tagsData || []);
        })
        .catch(() => {
          setTags([]);
        });

      // Populate form data from pkg prop if in edit mode
      if (pkg) {
        setFormData({
          name: pkg.name || "",
          description: pkg.description || "",
          type: pkg.type || "agent",
          isPublished: pkg.is_publish || false,
          selectedTags: pkg.tags || [],
        });
        setTagInput("");
        if (pkg.icon_uri) {
          setFileData((prev) => ({
            ...prev,
            iconPreview: pkg.icon_uri,
          }));
        }
      }

      setUiState((prev) => ({ ...prev, loading: false }));
    }
  }, [isOpen, pkg, t]);

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 500 * 1024) {
        setUiState((prev) => ({
          ...prev,
          error: t("marketplace.iconSizeLimit"),
        }));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFileData((prev) => ({
          ...prev,
          iconFile: file,
          iconPreview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setUiState((prev) => ({
          ...prev,
          error: t("marketplace.zipSizeLimit"),
        }));
        return;
      }
      setFileData((prev) => ({
        ...prev,
        zipFile: file,
        zipFileName: file.name,
      }));
      // Clear zip error when file is selected
      if (uiState.formErrors.zip) {
        setUiState((prev) => ({
          ...prev,
          formErrors: { ...prev.formErrors, zip: "" },
        }));
      }
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.selectedTags.includes(trimmedTag)) {
      setFormData((prev) => ({
        ...prev,
        selectedTags: [...prev.selectedTags, trimmedTag],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedTags: prev.selectedTags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleClearAllTags = () => {
    setFormData((prev) => ({
      ...prev,
      selectedTags: [],
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = t("marketplace.nameRequired");
    }
    if (!formData.description.trim()) {
      errors.description = t("marketplace.descriptionRequired");
    }
    if (!formData.type) {
      errors.type = t("marketplace.typeRequired");
    }
    if (!isEditMode && !fileData.zipFile) {
      errors.zip = t("marketplace.zipRequired");
    }

    setUiState((prev) => ({ ...prev, formErrors: errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    setUiState((prev) => ({ ...prev, loading: true, error: null }));

    // Use tags array directly
    const tagNames = formData.selectedTags;

    if (isEditMode) {
      // For update: is_publish should be PublishStatus
      const publishStatus: PublishStatus = formData.isPublished
        ? "public"
        : "draft";
      const payload = {
        name: formData.name,
        description: formData.description,
        type: formData.type as PackageType,
        is_publish: publishStatus,
        tag_names: tagNames.length > 0 ? tagNames : undefined,
        icon: fileData.iconFile || undefined,
        package: fileData.zipFile || undefined,
      };
      updatePackage(pkg!.id, payload)
        .then((response) => {
          if (!response?.data?.package_id) return;
          toast.success(t("marketplace.packageUpdated"));
          onClose();
        })
        .catch((err) => {
          handleAxiosAndCaughtError(err);
          setUiState((prev) => ({
            ...prev,
            error: t("marketplace.errorOccurred"),
            loading: false,
          }));
        })
        .finally(() => {
          setUiState((prev) => ({ ...prev, loading: false }));
        });
    } else {
      // For create: is_publish should be boolean
      const payload = {
        name: formData.name,
        description: formData.description,
        type: formData.type as PackageType,
        is_publish: formData.isPublished,
        tag_names: tagNames.length > 0 ? tagNames : undefined,
        icon: fileData.iconFile || undefined,
        package: fileData.zipFile || undefined,
      };
      createPackage(payload)
        .then((data) => {
          console.log("data", data);
          toast.success(t("marketplace.packageCreated"));
          onClose();
        })
        .catch((err) => {
          handleAxiosAndCaughtError(err);
          setUiState((prev) => ({
            ...prev,
            error: t("marketplace.errorOccurred"),
            loading: false,
          }));
        })
        .finally(() => {
          setUiState((prev) => ({ ...prev, loading: false }));
        });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      header={
        isEditMode
          ? t("marketplace.editPackage")
          : t("marketplace.createNewPackage")
      }
      subHeader={
        isEditMode
          ? t("marketplace.updateDetailsDescription")
          : t("marketplace.fillDetailsDescription")
      }
      className="min-w-[600px] max-w-2xl"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-4">
            {uiState.error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {uiState.error}
              </div>
            )}

            {/* Icon and Name section */}
            <div className="flex items-start gap-4">
              {/* Icon preview and upload */}
              <div className="flex flex-shrink-0 flex-col items-center">
                <label className="text-sm font-bold text-navy-700 dark:text-white">
                  {t("marketplace.iconLabel")}
                </label>
                <div className="mt-2">
                  <div
                    onClick={() =>
                      !uiState.loading && iconFileInputRef.current?.click()
                    }
                    className={`flex cursor-pointer justify-center${
                      uiState.loading ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  >
                    {fileData.iconPreview ? (
                      <img
                        src={fileData.iconPreview}
                        alt="Icon preview"
                        className="h-16 w-16 rounded-lg border border-gray-200 object-cover transition-colors hover:border-brand-500 dark:border-gray-700"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-brand-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700">
                        <span className="text-xs text-gray-400">Icon</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={iconFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIconChange}
                    disabled={uiState.loading}
                    className="hidden"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("marketplace.iconSizeHint")}
                  </p>
                </div>
              </div>

              {/* Name field */}
              <div className="flex-1">
                <InputField
                  id="name"
                  label={
                    <>
                      {t("common.name")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({ ...prev, name: e.target.value }));
                    if (uiState.formErrors.name) {
                      setUiState((prev) => ({
                        ...prev,
                        formErrors: { ...prev.formErrors, name: "" },
                      }));
                    }
                  }}
                  placeholder={t("marketplace.packageNamePlaceholder")}
                  disabled={uiState.loading}
                  state={uiState.formErrors.name ? "error" : undefined}
                />
                {uiState.formErrors.name && (
                  <p className="text-xs text-red-500">
                    {uiState.formErrors.name}
                  </p>
                )}
              </div>
            </div>

            {/* Publish Status and Type on same row */}
            <div className="grid grid-cols-2 gap-4">
              <div className={""}>
                <label
                  htmlFor="publish-status-toggle"
                  className="text-sm font-bold text-navy-700 dark:text-white"
                >
                  {t("marketplace.publishStatusLabel")}
                </label>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    id="publish-status-toggle"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    checked={formData.isPublished}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isPublished: e.target.checked,
                      }))
                    }
                    disabled={uiState.loading}
                  />
                  <label
                    htmlFor="publish-status-toggle"
                    className="text-sm text-gray-700 dark:text-gray-200"
                  >
                    {t("marketplace.statusPublic")}
                  </label>
                </div>
              </div>
              <div>
                <PureDropDown
                  options={packageTypes.map((pt) => ({
                    value: pt.value,
                    label: pt.label,
                  }))}
                  value={formData?.type?.toLowerCase() || undefined}
                  onChange={(value) => {
                    const selectedValue = Array.isArray(value)
                      ? value[0]
                      : value;
                    setFormData((prev) => ({
                      ...prev,
                      type: selectedValue as PackageType,
                    }));
                    setUiState((prev) => ({
                      ...prev,
                      formErrors: { ...prev.formErrors, type: "" },
                    }));
                  }}
                  placeholder={t("marketplace.selectType")}
                  label={
                    <>
                      {t("marketplace.typeLabel")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  disabled={uiState.loading}
                  showClearButton={false}
                  buttonClassName={
                    uiState.formErrors.type ? "border-red-500" : ""
                  }
                />
                {uiState.formErrors.type && (
                  <p className="mt-1 text-xs text-red-500">
                    {uiState.formErrors.type}
                  </p>
                )}
              </div>
            </div>

            {/* Package File (ZIP) with file name and upload button on same row */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <label className="text-sm font-bold text-navy-700 dark:text-white">
                {t("marketplace.zipFileLabel")}
                {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                  {fileData.zipFileName ? (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {fileData.zipFileName}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">
                      {t("marketplace.noFileSelected")}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => zipFileInputRef.current?.click()}
                  disabled={uiState.loading}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {t("common.upload")}
                </button>
                <input
                  ref={zipFileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleZipChange}
                  disabled={uiState.loading}
                  className="hidden"
                />
              </div>
              {uiState.formErrors.zip && (
                <p className="mt-2 text-xs text-red-500">
                  {uiState.formErrors.zip}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                {t("marketplace.zipSizeHint")}
              </p>
            </div>

            {/* Tags */}
            <div>
              <label className="text-sm font-bold text-navy-700 dark:text-white">
                {t("marketplace.tagsLabel")}
              </label>
              {formData.selectedTags.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {formData.selectedTags.map((tag) => (
                    <div
                      key={tag}
                      className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={uiState.loading}
                        className="ml-1 flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                        aria-label={`Remove ${tag}`}
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleClearAllTags}
                    disabled={uiState.loading}
                    className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700 transition-colors hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                  >
                    {t("common.clearAll")}
                  </button>
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTagInput(e.target.value)
                  }
                  onKeyPress={handleTagInputKeyPress}
                  placeholder={t("marketplace.tagsPlaceholder")}
                  disabled={uiState.loading}
                  className="mt-2 flex h-12 flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none disabled:!border-none disabled:!bg-gray-100 dark:!border-white/10 dark:text-white disabled:dark:!bg-white/5 disabled:dark:placeholder:!text-[rgba(255,255,255,0.15)]"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={uiState.loading || !tagInput.trim()}
                  className={`mt-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    tagInput.trim()
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {t("common.add")}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t("marketplace.tagsHint")}
              </p>
            </div>

            {/* Description */}
            <div>
              <TextField
                id="description"
                label={
                  <>
                    {t("common.description")}
                    <span className="text-red-500">*</span>
                  </>
                }
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                  if (uiState.formErrors.description) {
                    setUiState((prev) => ({
                      ...prev,
                      formErrors: { ...prev.formErrors, description: "" },
                    }));
                  }
                }}
                placeholder={t("marketplace.packageDescriptionPlaceholder")}
                cols={30}
                rows={5}
                disabled={uiState.loading}
                state={uiState.formErrors.description ? "error" : undefined}
              />
              {uiState.formErrors.description && (
                <p className="text-xs text-red-500">
                  {uiState.formErrors.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-shrink-0 justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={uiState.loading}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={uiState.loading}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uiState.loading ? (
              <span className="flex items-center gap-2">
                <AiOutlineLoading className="animate-spin" />
                {isEditMode ? t("common.saving") : t("common.creating")}
              </span>
            ) : isEditMode ? (
              t("common.saveChanges")
            ) : (
              t("common.create")
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PackageDialog;
